import React from 'react'
import { useSupercluster } from '../hooks'
import { FeaturesClusterMarker } from '../FeatureClusterMarker'
import { FeatureMarker } from '../FeatureMarker'
import {
  EstablishmentGeoJson,
  EstablishmentGeoJsonFeatureProperties
} from '../types'
import { isCluster } from './ClusteredMarker.helpers'
import Supercluster, { ClusterProperties } from 'supercluster'
import { GeoJsonProperties } from 'geojson'

type ClusteredMarkersProps = {
  geojson: EstablishmentGeoJson
  onClusterClick: (
    cluster: Supercluster.ClusterFeature<ClusterProperties>,
    marker: google.maps.marker.AdvancedMarkerElement,
    leaves: Supercluster.PointFeature<EstablishmentGeoJsonFeatureProperties>[]
  ) => void
  onMarkerClick: (
    feature: Supercluster.PointFeature<EstablishmentGeoJsonFeatureProperties>,
    marker: google.maps.marker.AdvancedMarkerElement
  ) => void
}

const superclusterOptions: Supercluster.Options<
  GeoJsonProperties,
  ClusterProperties
> = {
  maxZoom: 30,
  radius: 90
}

/**
 * This component utilizes the supercluster library to efficiently render
 * a large number of markers with the GeoJSON Specification on the map.
 *
 * - Supercluster: https://github.com/mapbox/supercluster
 * - VisGL example: https://github.com/visgl/react-google-maps/tree/main/examples/custom-marker-clustering
 * - GeoJSON Specification: https://geojson.org/
 */
const ClusteredMarkers = ({
  geojson,
  onClusterClick,
  onMarkerClick
}: ClusteredMarkersProps) => {
  const { clusters, getLeaves } = useSupercluster(geojson, superclusterOptions)

  return (
    <>
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates

        if (isCluster(feature)) {
          const { properties } = feature

          const count =
            Number(properties.point_count_abbreviated) > 99
              ? '99+'
              : String(properties.point_count_abbreviated)

          return (
            <FeaturesClusterMarker
              key={feature.id}
              clusterId={properties.cluster_id}
              position={{ lat, lng }}
              size={properties.point_count}
              count={count}
              onMarkerClick={marker => {
                const leaves = getLeaves(properties.cluster_id)
                onClusterClick(feature, marker, leaves)
              }}
            />
          )
        }

        return (
          <FeatureMarker
            key={feature.id}
            position={{ lat, lng }}
            properties={feature.properties}
            onMarkerClick={marker => onMarkerClick(feature, marker)}
          />
        )
      })}
    </>
  )
}

export { ClusteredMarkers }
